import api from "./api";

/**
 * Cliente da API de Modelos de Mensagem (`/api/templates`).
 *
 * Os "modelos de mensagem" são templates de WhatsApp Business usados pelo
 * `reminderService` para enviar lembretes de agendamento. A complexidade da
 * aprovação pela Meta é abstraída na UI — aqui só expomos as chamadas brutas.
 *
 * Ciclo de vida de um template:
 *   DRAFT → (submit) → PENDING → (sync) → APPROVED → (activate) → isActive
 *                                       → REJECTED  → (submit com correção)
 *
 * Observações de contrato relevantes ao caller:
 * - `createTemplate` pode retornar 201 com um campo extra `warning` (string)
 *   quando o WhatsApp não está conectado: nesse caso o backend salva como
 *   DRAFT. O caller deve exibir esse aviso ao usuário.
 * - `submitTemplate` aceita body opcional `{ bodyText?, category? }`: sem body
 *   reenvia o texto salvo (reenvio de DRAFT); com body corrige antes de
 *   reenviar (fluxo "Corrigir e reenviar" de REJECTED).
 */

/**
 * Lista todos os modelos do tenant autenticado.
 * Backend retorna array ordenado por `createdAt desc`.
 *
 * @param {import("axios").AxiosRequestConfig} [config] config opcional do axios
 *   (ex.: `{ signal }` para cancelamento via AbortController).
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const listTemplates = (config = {}) => api.get("/templates", config);

/**
 * Cria um novo modelo de mensagem.
 *
 * Se o WhatsApp não estiver conectado, o backend salva como DRAFT e devolve o
 * template com um campo extra `warning` (string) — trate-o como aviso, não erro.
 *
 * @param {{ displayName: string, bodyText: string, category: string }} data
 *   `bodyText` já deve estar em formato backend (`{{1}}`/`{{2}}`/`{{3}}`).
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const createTemplate = (data) => api.post("/templates", data);

/**
 * Re-submete um modelo (DRAFT ou REJECTED) à aprovação.
 *
 * @param {string} id
 * @param {{ bodyText?: string, category?: string }} [data] body opcional.
 *   Omitido → reenvia o texto salvo. Presente → corrige antes de reenviar.
 *   `bodyText`, quando informado, já deve estar em formato backend.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const submitTemplate = (id, data) =>
  api.post(`/templates/${id}/submit`, data);

/**
 * Sincroniza o status do modelo com a Meta (APPROVED / REJECTED / PENDING).
 * Pode auto-ativar se aprovado e nenhum outro modelo estiver ativo.
 *
 * @param {string} id
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const syncTemplate = (id) => api.post(`/templates/${id}/sync`);

/**
 * Ativa um modelo (só funciona se `status === "APPROVED"`); desativa os outros.
 *
 * @param {string} id
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const activateTemplate = (id) => api.patch(`/templates/${id}/activate`);

/**
 * Remove um modelo localmente (e na Meta, se já tiver `metaTemplateId`).
 *
 * @param {string} id
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);
